import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Box, SxProps } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { UiContext } from 'shared/lib/contexts/uiContext';
import { ChangeEvent, useContext } from 'react';
import CustomTextField from 'shared/lib/components/controls/textFields/customTextField';
import * as yup from 'yup';
import { useFormik } from 'formik';
import EmailTextField from 'shared/lib/components/controls/textFields/emailTextField';
import { useTranslation } from 'react-i18next';
import { isEqual } from 'lodash-es';
import {
  EditProfileData,
  ProfileModel,
} from 'entities/profile/model/userModel';
import { regulars } from 'shared/lib/utils/globalUtils';

const NAME_MAX_LENGTH = 100;
const SURNAME_MAX_LENGTH = 100;

export default observer(function ProfileEditForm({
  user,
  id,
  setHasProfileEditFormChanged,
  setIsLoading,
  handleSubmit,
  sx,
  emailError,
  resetEmailError,
  formikSubmited,
}: {
  user: ProfileModel;
  id: string;
  setHasProfileEditFormChanged: Dispatch<SetStateAction<boolean>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  handleSubmit: (formValues: EditProfileData) => void;
  sx?: SxProps;
  emailError?: 'emailAlreadyExists' | null;
  resetEmailError: () => void;
  formikSubmited: boolean;
}) {
  const { t } = useTranslation();
  const {
    spacings: {
      sp,
      profilePage: { profileEdit },
    },
  } = useContext(UiContext);

  const initialValues = useMemo(
    () => ({
      name: user.name,
      surname: user.surname,
      email: user.email,
    }),
    [user.email, user.name, user.surname]
  );

  const validationSchema = yup.object({
    name: yup
      .string()
      .required(t('errors.validationSchema.required'))
      .matches(
        regulars.onlyKirilic,
        t('errors.validationSchema.invalidSymbols')
      )
      .max(
        NAME_MAX_LENGTH,
        t('errors.validationSchema.nameTooLong', { maxLength: NAME_MAX_LENGTH })
      ),
    surname: yup
      .string()
      .required(t('errors.validationSchema.required'))
      .matches(
        regulars.onlyKirilic,
        t('errors.validationSchema.invalidSymbols')
      )
      .max(
        SURNAME_MAX_LENGTH,
        t('errors.validationSchema.surnameTooLong', {
          maxLength: SURNAME_MAX_LENGTH,
        })
      ),
    email: yup.string().required(t('errors.validationSchema.required')).email(),
  });

  const formik = useFormik({
    initialValues,
    validateOnBlur: false,
    validateOnChange: formikSubmited,
    validationSchema: validationSchema,
    onSubmit: async (formValues) => {
      try {
        setIsLoading(true);
        await handleSubmit(formValues);
        setIsLoading(false);
      } catch (e) {
        console.error(e);
        setIsLoading(false);
      }
    },
  });

  useEffect(() => {
    if (!isEqual(formik.values, initialValues)) {
      setHasProfileEditFormChanged(true);
    } else {
      setHasProfileEditFormChanged(false);
    }
  }, [formik.values, initialValues, setHasProfileEditFormChanged]);

  const isFormikFieldError = (field: keyof typeof formik.initialValues) =>
    formik.touched[field] && !!formik.errors[field];

  const getFormikFieldErrorText = (field: keyof typeof formik.initialValues) =>
    formik.touched[field] && formik.errors[field];

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(e);
  };

  return (
    <Box
      component="form"
      id={id}
      sx={{
        width: profileEdit.formWidth,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: sp.s50,
        ...sx,
      }}
      onSubmit={formik.handleSubmit}
    >
      <CustomTextField
        label={t('login.name')}
        id="name"
        name="name"
        typographyVariantSize="f16"
        labelTypographyVariantSize="f12"
        placeholder={t('login.namePlaceholder')}
        wrapperSxProps={{
          width: 1,
        }}
        value={formik.values.name}
        onChange={handleOnChange}
        onBlur={formik.handleBlur}
        error={isFormikFieldError('name')}
        helperText={getFormikFieldErrorText('name')}
        autoFocus={true}
        isClearIcon
      ></CustomTextField>

      <CustomTextField
        label={t('login.surname')}
        id="surname"
        name="surname"
        typographyVariantSize="f16"
        labelTypographyVariantSize="f12"
        placeholder={t('login.surnamePlaceholder')}
        wrapperSxProps={{
          width: 1,
        }}
        value={formik.values.surname}
        onChange={handleOnChange}
        onBlur={formik.handleBlur}
        error={isFormikFieldError('surname')}
        helperText={getFormikFieldErrorText('surname')}
        isClearIcon
      ></CustomTextField>

      <EmailTextField
        label={t('login.email')}
        id="email"
        name="email"
        placeholder={t('login.emailPlaceholder')}
        value={formik.values.email}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          resetEmailError();
          handleOnChange(e);
        }}
        onBlur={formik.handleBlur}
        error={isFormikFieldError('email') || Boolean(emailError)}
        helperText={
          getFormikFieldErrorText('email') ||
          (emailError && t(`errors.${emailError}`))
        }
        isClearIcon
      ></EmailTextField>
    </Box>
  );
});
